/**
 * Tooltip markup builder + money formatter, ported from chart-tip.jsx.
 * `tipHTML` returns an HTML string rendered by TooltipProvider via
 * dangerouslySetInnerHTML — the same trusted-content model as the mockup
 * (titles/keys are escaped; values/rows may carry our own formatted HTML).
 */
import { fmtM } from '@/lib/format';

export interface TipRow {
  k: string;
  /** May contain trusted HTML (e.g. an up/down span). */
  v: string;
  /** Optional color swatch square before the key. */
  swatch?: string;
  /** Optional value color. */
  c?: string;
}

export interface TipObject {
  title?: string;
  /** Accent dot + value color. */
  accent?: string;
  /** May contain trusted HTML. */
  value?: string;
  sub?: string;
  rows?: TipRow[];
  note?: string;
}

export type TipInput = string | TipObject;

function esc(s: unknown): string {
  return String(s == null ? '' : s).replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
  );
}

/** Build tooltip inner HTML from a string or structured object. */
export function tipHTML(o: TipInput | null | undefined): string {
  if (o == null) return '';
  if (typeof o === 'string') {
    return `<div class="bs-tip-val" style="font-size:13px;font-weight:600">${esc(o)}</div>`;
  }
  let h = '';
  if (o.title) {
    h += `<div class="bs-tip-hd">${
      o.accent ? `<span class="bs-tip-dot" style="background:${o.accent};color:${o.accent}"></span>` : ''
    }${esc(o.title)}</div>`;
  }
  if (o.value != null) {
    h += `<div class="bs-tip-val"${o.accent ? ` style="color:${o.accent}"` : ''}>${o.value}</div>`;
  }
  if (o.sub) h += `<div class="bs-tip-sub">${o.sub}</div>`;
  if (o.rows && o.rows.length) {
    h +=
      `<div class="bs-tip-rows">` +
      o.rows
        .map(
          (r) =>
            `<div class="bs-tip-row"><span class="bs-tip-row-k">${
              r.swatch ? `<i style="background:${r.swatch}"></i>` : ''
            }${esc(r.k)}</span><span class="bs-tip-row-v"${r.c ? ` style="color:${r.c}"` : ''}>${r.v}</span></div>`,
        )
        .join('') +
      `</div>`;
  }
  if (o.note) h += `<div class="bs-tip-note">${o.note}</div>`;
  return h;
}

/** "$1,234" · "−$1,234" — money for tooltip rows/values. Thin alias over fmtM. */
export function tipMoney(n: number, dec = 0): string {
  return fmtM(n, { decimals: dec });
}
